import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Package, Plus, Edit2, Trash2, Loader2, ChevronDown, ChevronUp,
  CheckCircle2, IndianRupee, Tag,
} from 'lucide-react';
import { api } from '@/lib/api';
import { clsx } from 'clsx';

const CATEGORIES = ['maternity','surgery','day_care','icu','diagnostic','other'] as const;

const CAT_CONFIG: Record<string, { label: string; color: string }> = {
  maternity:  { label: 'Maternity',   color: 'bg-pink-50 text-pink-700 border-pink-200' },
  surgery:    { label: 'Surgery',     color: 'bg-red-50 text-red-700 border-red-200' },
  day_care:   { label: 'Day Care',    color: 'bg-blue-50 text-blue-700 border-blue-200' },
  icu:        { label: 'ICU',         color: 'bg-purple-50 text-purple-700 border-purple-200' },
  diagnostic: { label: 'Diagnostic',  color: 'bg-teal-50 text-teal-700 border-teal-200' },
  other:      { label: 'Other',       color: 'bg-gray-50 text-gray-600 border-gray-200' },
};

const ITEM_CATEGORIES = ['consultation','bed','pathology','radiology','procedure','pharmacy','nursing','diet','other'];

interface PackageItem { service_name: string; category: string; quantity: number; unit_rate: number; is_included: boolean; notes: string; sort_order: number; }
interface PackageForm { package_name: string; package_code: string; category: string; description: string; package_price: number | string; validity_days: number | string; is_active: boolean; items: PackageItem[]; }

const emptyItem = (): PackageItem => ({ service_name: '', category: 'procedure', quantity: 1, unit_rate: 0, is_included: true, notes: '', sort_order: 0 });

function PackageModal({ pkg, onClose }: { pkg?: any; onClose: () => void }) {
  const qc = useQueryClient();
  const isEdit = !!pkg;

  const [form, setForm] = useState<PackageForm>({
    package_name:  pkg?.package_name ?? '',
    package_code:  pkg?.package_code ?? '',
    category:      pkg?.category ?? 'surgery',
    description:   pkg?.description ?? '',
    package_price: pkg?.package_price ?? '',
    validity_days: pkg?.validity_days ?? 30,
    is_active:     pkg?.is_active ?? true,
    items: pkg?.items ?? [emptyItem()],
  });

  const saveMutation = useMutation({
    mutationFn: (data: any) =>
      isEdit ? api.put(`/billing/packages/${pkg.id}`, data) : api.post('/billing/packages', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['billing-packages'] }); onClose(); },
  });

  function setField(k: keyof PackageForm, v: any) { setForm(f => ({ ...f, [k]: v })); }
  function setItem(idx: number, k: keyof PackageItem, v: any) {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [k]: v };
      return { ...f, items };
    });
  }
  function addItem() { setForm(f => ({ ...f, items: [...f.items, { ...emptyItem(), sort_order: f.items.length }] })); }
  function removeItem(idx: number) { setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) })); }

  const totalItemsValue = form.items.reduce((s, i) => s + (Number(i.quantity) * Number(i.unit_rate)), 0);

  function submit() {
    saveMutation.mutate({
      ...form,
      package_price: Number(form.package_price),
      validity_days: Number(form.validity_days),
      items: form.items.map((item, idx) => ({
        ...item,
        quantity: Number(item.quantity),
        unit_rate: Number(item.unit_rate),
        sort_order: idx,
      })),
    });
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-base font-bold text-navy flex items-center gap-2">
            <Package size={16} className="text-orange" />
            {isEdit ? 'Edit Package' : 'New Billing Package'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-light">×</button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600 block mb-1">Package Name *</label>
              <input value={form.package_name} onChange={e => setField('package_name', e.target.value)}
                placeholder="e.g. Normal Delivery Package, Appendicectomy Package..."
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Package Code *</label>
              <input value={form.package_code} onChange={e => setField('package_code', e.target.value.toUpperCase())}
                placeholder="PKG-MAT-NV"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none font-mono" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Category *</label>
              <select value={form.category} onChange={e => setField('category', e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none">
                {CATEGORIES.map(c => <option key={c} value={c} className="capitalize">{CAT_CONFIG[c]?.label ?? c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Package Price (₹) *</label>
              <input type="number" value={form.package_price} onChange={e => setField('package_price', e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Validity (days)</label>
              <input type="number" min="1" value={form.validity_days} onChange={e => setField('validity_days', e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600 block mb-1">Description</label>
              <textarea value={form.description} onChange={e => setField('description', e.target.value)}
                rows={2} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none resize-none" />
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-600">Package Contents *</label>
              <div className="flex items-center gap-3 text-xs text-gray-400">
                <span>Items total: <span className="font-semibold text-navy">₹{totalItemsValue.toLocaleString('en-IN')}</span></span>
                {Number(form.package_price) > 0 && totalItemsValue > 0 && (
                  <span>Discount: <span className="font-semibold text-green-600">
                    {(((totalItemsValue - Number(form.package_price)) / totalItemsValue) * 100).toFixed(1)}%
                  </span></span>
                )}
              </div>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {form.items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-xl p-2">
                  <div className="flex-1 min-w-0">
                    <input value={item.service_name} onChange={e => setItem(idx, 'service_name', e.target.value)}
                      placeholder="Service name..."
                      className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-orange/30 outline-none mb-1.5" />
                    <div className="flex gap-2">
                      <select value={item.category} onChange={e => setItem(idx, 'category', e.target.value)}
                        className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white outline-none flex-1">
                        {ITEM_CATEGORIES.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
                      </select>
                      <input type="number" value={item.quantity} onChange={e => setItem(idx, 'quantity', e.target.value)}
                        placeholder="Qty" className="w-14 border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white outline-none" />
                      <input type="number" value={item.unit_rate} onChange={e => setItem(idx, 'unit_rate', e.target.value)}
                        placeholder="Rate" className="w-20 border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white outline-none" />
                      <label className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                        <input type="checkbox" checked={item.is_included} onChange={e => setItem(idx, 'is_included', e.target.checked)} />
                        Included
                      </label>
                    </div>
                  </div>
                  <button onClick={() => removeItem(idx)}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition flex-shrink-0">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>

            <button onClick={addItem}
              className="mt-2 flex items-center gap-1.5 text-xs text-orange hover:text-orange/80 font-medium transition">
              <Plus size={13} /> Add Item
            </button>
          </div>
        </div>

        <div className="px-5 pb-5 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition">Cancel</button>
          <button
            onClick={submit}
            disabled={saveMutation.isPending || !form.package_name || !form.package_code || !form.package_price}
            className="flex-1 py-2 bg-orange text-white rounded-xl text-sm font-semibold disabled:opacity-40 hover:bg-orange/90 flex items-center justify-center gap-2 transition">
            {saveMutation.isPending && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? 'Update Package' : 'Create Package'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PackageCard({ pkg, onEdit, onDelete }: any) {
  const [expanded, setExpanded] = useState(false);
  const catCfg = CAT_CONFIG[pkg.category] ?? CAT_CONFIG.other;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={clsx('text-xs px-2 py-0.5 rounded-full border font-medium', catCfg.color)}>
                {catCfg.label}
              </span>
              <span className="text-xs font-mono text-gray-400">{pkg.package_code}</span>
              {!pkg.is_active && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">Inactive</span>
              )}
            </div>
            <h3 className="font-semibold text-navy">{pkg.package_name}</h3>
            {pkg.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{pkg.description}</p>}
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-lg font-bold text-navy">₹{Number(pkg.package_price).toLocaleString('en-IN')}</p>
            <p className="text-xs text-gray-400">Valid {pkg.validity_days} days</p>
          </div>
        </div>

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <span className="flex items-center gap-1"><Tag size={11} />{pkg.item_count ?? 0} items</span>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setExpanded(e => !e)}
              className="p-1.5 text-gray-400 hover:text-navy hover:bg-gray-100 rounded-lg transition text-xs flex items-center gap-1">
              {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              Items
            </button>
            <button onClick={() => onEdit(pkg)}
              className="p-1.5 text-gray-400 hover:text-navy hover:bg-gray-100 rounded-lg transition">
              <Edit2 size={13} />
            </button>
            <button onClick={() => onDelete(pkg.id)}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition">
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      </div>

      {expanded && pkg.items && (
        <div className="border-t border-gray-50 bg-gray-50/50 p-3">
          <div className="space-y-1.5">
            {pkg.items.map((item: any, idx: number) => (
              <div key={idx} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  {item.is_included
                    ? <CheckCircle2 size={11} className="text-green-500 flex-shrink-0" />
                    : <span className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0 inline-block" />
                  }
                  <span className="text-gray-700">{item.service_name}</span>
                  <span className="text-gray-400">×{item.quantity}</span>
                </div>
                <span className="text-gray-500">₹{(item.quantity * item.unit_rate).toLocaleString('en-IN')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PackagesPage() {
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editPkg, setEditPkg] = useState<any>(null);
  const [catFilter, setCatFilter] = useState('');

  const { data: packages, isLoading } = useQuery({
    queryKey: ['billing-packages', catFilter],
    queryFn: () => api.get('/billing/packages', { params: { category: catFilter || undefined } }).then(r => r.data),
  });

  // Fetch package items for expanded view
  const { data: pkgDetails } = useQuery({
    queryKey: ['billing-packages-detail'],
    queryFn: async () => {
      if (!packages?.length) return {};
      const details: Record<string, any> = {};
      await Promise.all((packages ?? []).slice(0, 20).map(async (p: any) => {
        const res = await api.get(`/billing/packages/${p.id}`).then(r => r.data);
        details[p.id] = res.items;
      }));
      return details;
    },
    enabled: !!packages?.length,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/billing/packages/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing-packages'] }),
  });

  const pkgList: any[] = (packages ?? []).map((p: any) => ({
    ...p,
    items: pkgDetails?.[p.id] ?? [],
  }));

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
            <Package size={22} className="text-orange" /> Billing Packages
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Bundled procedure packages — maternity, surgery, day-care</p>
        </div>
        <button
          onClick={() => { setEditPkg(null); setShowModal(true); }}
          className="flex items-center gap-2 bg-orange text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-orange/90 transition shadow-sm">
          <Plus size={15} /> New Package
        </button>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={() => setCatFilter('')}
          className={clsx('px-3 py-1.5 rounded-xl text-xs font-medium border transition',
            !catFilter ? 'bg-navy text-white border-navy' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300')}>
          All
        </button>
        {CATEGORIES.map(c => (
          <button key={c} onClick={() => setCatFilter(c)}
            className={clsx('px-3 py-1.5 rounded-xl text-xs font-medium border transition capitalize',
              catFilter === c
                ? 'bg-navy text-white border-navy'
                : clsx('bg-white', CAT_CONFIG[c]?.color ?? 'border-gray-200 text-gray-600'))}>
            {CAT_CONFIG[c]?.label ?? c}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16"><Loader2 size={22} className="animate-spin text-orange" /></div>
      ) : pkgList.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card flex flex-col items-center justify-center py-16 text-gray-400">
          <Package size={40} className="mb-3 opacity-25" />
          <p className="text-base font-medium text-navy">No packages configured</p>
          <button onClick={() => setShowModal(true)}
            className="mt-3 flex items-center gap-1.5 text-sm text-orange hover:underline">
            <Plus size={13} /> Create your first package
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {pkgList.map((pkg: any) => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              onEdit={(p: any) => { setEditPkg(p); setShowModal(true); }}
              onDelete={(id: string) => { if (confirm('Delete this package?')) deleteMutation.mutate(id); }}
            />
          ))}
        </div>
      )}

      {showModal && (
        <PackageModal
          pkg={editPkg}
          onClose={() => { setShowModal(false); setEditPkg(null); }}
        />
      )}
    </div>
  );
}
